import { Alert, FormControl, Grid, Paper, TextField } from "@mui/material";
import { MRT_ColumnDef } from "material-react-table";
import { useEffect, useMemo, useState } from "react";
import { ValidatorForm } from "react-material-ui-form-validator";
import { useNavigate } from "react-router-dom";
import { FormTemplateTypeEnum } from "../_common/enum/forms/fomTemplateType.enum";
import { FormPriorityEnum } from "../_common/enum/forms/formPriority.enum";
import { FormStatusEnum } from "../_common/enum/forms/formStatus.enum";
import { IRacingLicenseGradeList } from "../_common/interface/racingLicenseGrade/iRacingLicenseGradeList";
import { IFilteringViewModel, ITableData } from "../_common/interface/table";
import StyledGrid from "../_components/grid/styledGrid";
import { PageHeader } from "../_components/header/page.header";
import { CommonTable } from "../_components/table/common.table";
import { getDateTime } from "../_helpers/dateTime.helper";
import { getEnumDropdown } from "../_helpers/form.helper";
import { handleErrorResponse } from "../_helpers/handleResponse.helper";
import { formService } from "../_services/form.service";
import { useContainerStyles } from "../_styles/container.styles";
import { PrimaryButton } from "../_components/button/primary.button";
import { FormStatusAdminEnum } from "../_common/enum/forms/formStatusAdmin.enum";

export function FormIndex() {

    const [data, setData] = useState<ITableData<IRacingLicenseGradeList>>({ items: [], count: 0 });
    const [isLoading, setLoading] = useState(true);
    const [filter, setFilter] = useState<IFilteringViewModel | null>(null);

    const [search, setSearch] = useState<any>({
        formStatusId: "",
        userName: "",
        formTemplateTypeId: "",
        fromDate: "",
        toDate: ""
    });

    let navigate = useNavigate();

    const columns = useMemo<MRT_ColumnDef<any>[]>(
        () => [
            {
                accessorKey: 'userName',
                header: 'User Name',
            },
            {
                accessorKey: 'formPriorityId',
                header: 'Priority',
                accessorFn: (rowData: any) => <>
                    {
                        rowData.formPriorityId == FormPriorityEnum.SameDay ? <Alert severity="error">{FormPriorityEnum[rowData.formPriorityId]}</Alert> :
                            rowData.formPriorityId == FormPriorityEnum.Priority ? <Alert severity="warning">{FormPriorityEnum[rowData.formPriorityId]}</Alert> :
                                rowData.formPriorityId == FormPriorityEnum.Standard ? <Alert severity="info">{FormPriorityEnum[rowData.formPriorityId]}</Alert> :
                                    <></>}
                </>,
            },
            {
                accessorKey: 'completedDate',
                header: 'Date Completed',
                accessorFn: (rowData: any) => <>{rowData.completedDate ? getDateTime(rowData.completedDate) : "-"}</>,
            },
            {
                accessorKey: 'name',
                header: 'Name',
            },
            {
                accessorKey: 'formStatusAdminId',
                header: 'Form Admin Status',
                accessorFn: (rowData: any) => <>
                    {
                        rowData.formStatusAdminId == FormStatusAdminEnum.Approved ? <Alert severity="success">{FormStatusAdminEnum[rowData.formStatusId]}</Alert> :
                            rowData.formStatusAdminId == FormStatusAdminEnum.MissingItems ? <Alert severity="warning">{FormStatusAdminEnum[rowData.formStatusId]}</Alert> :
                                rowData.formStatusAdminId == FormStatusAdminEnum.Unprocessed ? <Alert severity="warning">{FormStatusAdminEnum[rowData.formStatusId]}</Alert> :
                                    rowData.formStatusAdminId == FormStatusAdminEnum.UserUpdated ? <Alert severity="warning">{FormStatusAdminEnum[rowData.formStatusId]}</Alert> :
                                        rowData.formStatusAdminId == FormStatusAdminEnum.Cancelled ? <Alert severity="error">{FormStatusAdminEnum[rowData.formStatusId]}</Alert> :
                                            <>-</>}
                </>,
            },
            {
                accessorKey: 'formStatusId',
                header: 'Form Status',
                accessorFn: (rowData: any) => <>
                    {
                        rowData.formStatusId == FormStatusEnum.CompletedPaid ? <Alert severity="success">{FormStatusEnum[rowData.formStatusId]}</Alert> :
                            rowData.formStatusId == FormStatusEnum.CompletedNotPaid ? <Alert severity="warning">{FormStatusEnum[rowData.formStatusId]}</Alert> :
                                <></>}
                </>,
            },
            {
                accessorKey: 'formTypeName',
                header: 'Form Type',
            },

            {
                accessorKey: 'comment',
                header: 'Comments',
            },

        ],
        [],
    );

    useEffect(() => {
        if (filter) fetchData();
    }, [filter]);

    function handleChange(event: any) {
        setSearch((__filter: any) => { return { ...__filter, [event.target.name]: event.target.value } });
    };

    function fetchData() {
        setLoading(true);
        return formService.getAll({ ...filter, ...search })
            .then(
                data => {
                    setData(data);
                }, handleErrorResponse)
            .finally(() => {
                setLoading(false);
            });
    }

    return (
        <div style={useContainerStyles.root}>
            <PageHeader pageName="Forms" />

            <Paper>
                <ValidatorForm onSubmit={fetchData}>
                    <StyledGrid container spacing={2}>
                        <Grid item lg={2} md={6} xs={12}>
                            <FormControl fullWidth>
                                <TextField label="User Name" name="userName" value={search.userName} onChange={handleChange} placeholder="User Name" variant="outlined" />
                            </FormControl>
                        </Grid>
                        <Grid item lg={2} md={6} xs={12}>
                            {getEnumDropdown("Form Status", "formStatusId", FormStatusEnum, search["formStatusId"], handleChange, false, "outlined")}
                        </Grid>
                        <Grid item lg={2} md={6} xs={12}>
                            {getEnumDropdown("Form Type", "formTemplateTypeId", FormTemplateTypeEnum, search["formTemplateTypeId"], handleChange, false, "outlined")}
                        </Grid>
                        <Grid item lg={2} md={6} xs={12}>
                            <FormControl fullWidth>
                                <TextField
                                    type="date"
                                    label="From Date"
                                    name="fromDate"
                                    value={search.fromDate}
                                    onChange={handleChange}
                                    placeholder="From Date"
                                    variant="outlined"
                                    InputLabelProps={{ shrink: true }}
                                />
                            </FormControl>
                        </Grid>
                        <Grid item lg={2} md={6} xs={12}>
                            <FormControl fullWidth>
                                <TextField
                                    type="date"
                                    label="To Date"
                                    name="toDate"
                                    value={search.toDate}
                                    onChange={handleChange}
                                    placeholder="To Date"
                                    variant="outlined"
                                    InputLabelProps={{ shrink: true }}
                                />
                            </FormControl>
                        </Grid>
                        <Grid item lg={2} md={6} xs={12}>
                            <PrimaryButton
                                fullHeight
                                isLoading={isLoading}
                                type="submit"
                                variant="save">{"Search"}
                            </PrimaryButton>
                        </Grid>
                    </StyledGrid>
                </ValidatorForm>

                <CommonTable
                    columns={columns}
                    data={data}
                    onEdit={(id) => { (window as any).open("Forms/" + id || "", '_blank').focus(); }}
                    onView={(item) => { (window as any).open("Users/" + item.userId || "", '_blank').focus(); }}
                    setFilter={setFilter}
                    isLoading={isLoading}
                />
            </Paper>

        </div>
    );

}
